import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import React, { useState, useEffect } from "react";
import {
  FlaskConical,
  Plus,
  Trash2,
  Search,
  Package,
  FileDown,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Settings,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { useConfigDocumentoSalva } from "../hooks/useConfigDocumentoSalva";
import {
  catalogoExames,
  conjuntosPadrao,
  buscarExames,
  buscarExamePorNome,
  ConjuntoExames,
  ExameTUSS,
} from "../data/examesTUSS";
import {
  gerarPDFPedidoExames,
  montarPedidoExamesHTML,
} from "../services/gerarPDF";
import {
  montarFragmentoFichaGAL,
  SolicitanteGAL,
  DadosPedidoGAL,
} from "../services/gerarFichaGAL";
import {
  montarFragmentoCargaViral,
  configCargaViral,
  IDADE_GESTACIONAL_OPCOES,
  ANTI_HDV_RESULTADO_OPCOES,
  DadosCargaViral,
} from "../services/gerarFichaCargaViral";
import { montarFragmentoAPAC, DadosAPAC } from "../services/gerarFichaAPAC";
import {
  formulariosParaExames,
  FormularioAuxiliar,
  exigeAPAC,
  sigtapPorTuss,
} from "../data/formulariosAuxiliares";
import { useSorologias } from "../../sorologias/context/SorologiasContext";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import ModalAssinatura from "./ModalAssinatura";
import { DadosAssinaturaWidget } from "../services/assinaturaCliente";
import {
  montarSeloAssinaturaDigital,
  dataHoraSeloAgora,
} from "../services/seloAssinatura";
import {
  gerarCodigoValidacao,
  montarBlocoQR,
} from "../../validacoes/services/validacaoCliente";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import { removerUndefined } from "../../../shared/utils/removerUndefined";

// B289/B212 (AI_131): GO-LIVE DE PRODUÇÃO CONCLUÍDO (29/06/2026) → assinatura
// habilitada para todos os médicos (o gate `ehMedico` abaixo barra não-médicos).
// Antes ficava atrás da flag local `assinar_habilitado` (só nos navegadores de teste).
const ASSINATURA_HABILITADA = true;

interface PedidoExamesProps {
  paciente: Paciente;
  dadosIniciais?: any;
  onPrescricaoSalva?: () => void;
}
export default function PedidoExames({
  paciente,
  dadosIniciais,
  onPrescricaoSalva,
}: PedidoExamesProps) {
  const { usuarioLogado } = useAuth();
  const { listarSorologiasPorPaciente } = useSorologias();
  const [examesSelecionados, setExamesSelecionados] = useState<ExameTUSS[]>([]);
  const [termoBusca, setTermoBusca] = useState("");
  const [resultadosBusca, setResultadosBusca] = useState<ExameTUSS[]>([]);
  const [conjuntosUsuario, setConjuntosUsuario] = useState<ConjuntoExames[]>(
    []
  );
  const [mostrarConjuntos, setMostrarConjuntos] = useState(true);
  const [mostrarNovoConjunto, setMostrarNovoConjunto] = useState(false);
  const [nomeNovoConjunto, setNomeNovoConjunto] = useState("");
  const [observacao, setObservacao] = useState("");
  const [obsImagem, setObsImagem] = useState<Record<string, string>>({});
  const [cidade, setCidade] = useState("Porto Velho - RO");
  const [telefone, setTelefone] = useState("(69) 3216-5700");

  // 🏥 Formulários auxiliares do SUS (B211) — ficha GAL (CMV)
  const [galModalAberto, setGalModalAberto] = useState(false);
  const [dadosGAL, setDadosGAL] = useState<DadosPedidoGAL | null>(null);
  // 🏥 Carga viral (HBV/HCV/HIV/Delta) + Genotipagem do VHC — modelo gov.br/aids.
  // Vários formulários podem coexistir num mesmo pedido (ex.: genotipagem +
  // carga viral do HCV). Guardamos um por formularioId e qual modal está aberto.
  const [cvModalAbertoId, setCvModalAbertoId] = useState<string | null>(null);
  const [dadosCVPorForm, setDadosCVPorForm] = useState<
    Record<string, DadosCargaViral>
  >({});
  // 🏥 APAC (imagem de média/alta complexidade) — UM laudo POR exame de imagem
  // (Leonardo: ex. TC de tórax + TC de abdome = 2 APACs, um modal cada).
  const [apacModalAbertoTuss, setApacModalAbertoTuss] = useState<string | null>(
    null
  );
  const [dadosAPACPorExame, setDadosAPACPorExame] = useState<
    Record<string, DadosAPAC>
  >({});

  // B289 (AI_127): dados do modal de assinatura digital (null = fechado).
  const [modalAssinatura, setModalAssinatura] = useState<{
    html: string;
    tipo: string;
    prescricaoId: string;
    dados: DadosAssinaturaWidget;
    nomeArquivo: string;
    codigoValidacao?: string;
    dadosValidacao?: {
      medicoNome: string;
      registro: string;
      pacienteTelefone: string;
      pacienteNascimento: string;
    };
  } | null>(null);

  // Carregar dados do histórico quando clicar num card
  useEffect(() => {
    if (dadosIniciais?.conteudo) {
      const exames = dadosIniciais.conteudo.exames || dadosIniciais.conteudo;
      if (Array.isArray(exames)) {
        const examesTipados = exames.map((e: any) => ({
          nome: e.nome,
          tuss: e.tuss,
          categoria: e.categoria || ("laboratorial" as const),
        }));
        setExamesSelecionados(examesTipados);
      }
      if (dadosIniciais.conteudo.observacao) {
        setObservacao(dadosIniciais.conteudo.observacao);
      }
      // 🏥 Restaurar formulários auxiliares salvos junto ao pedido (reaproveitamento)
      const auxiliares = dadosIniciais.conteudo.formulariosAuxiliares;
      if (Array.isArray(auxiliares)) {
        const galSalvo = auxiliares.find((f: any) => f.tipo === "gal");
        if (galSalvo?.dados) setDadosGAL(galSalvo.dados);
        // Pode haver mais de um formulário de carga viral salvo (ex.:
        // genotipagem + carga viral do HCV).
        const novoMapaCV: Record<string, DadosCargaViral> = {};
        auxiliares
          .filter((f: any) => f.tipo === "cargaViral")
          .forEach((f: any) => {
            const id = f.formularioId || f.dados?.formularioId;
            if (id && f.dados) novoMapaCV[id] = f.dados;
          });
        if (Object.keys(novoMapaCV).length) setDadosCVPorForm(novoMapaCV);
        // APAC: um laudo por exame de imagem (keyed pelo TUSS do exame).
        const novoMapaAPAC: Record<string, DadosAPAC> = {};
        auxiliares
          .filter((f: any) => f.tipo === "apac")
          .forEach((f: any) => {
            if (f.tuss && f.dados) novoMapaAPAC[f.tuss] = f.dados;
          });
        if (Object.keys(novoMapaAPAC).length)
          setDadosAPACPorExame(novoMapaAPAC);
      }
      if (dadosIniciais.config?.nomeAmbulatorio) {
        setNomeAmbulatorio(dadosIniciais.config.nomeAmbulatorio);
      }
      if (dadosIniciais.config?.endereco) {
        setEndereco(dadosIniciais.config.endereco);
      }
    }
  }, [dadosIniciais]);

  // Config do PDF
  const [nomeAmbulatorio, setNomeAmbulatorio] = useState(
    "Ambulatório de Transplante Hepático de Rondônia"
  );
  const [endereco, setEndereco] = useState("Av. Gov. Jorge Teixeira, 3766");
  // AI_154 — configuração do documento salva por usuário (usuarios/{uid}).
  const { marcarEdicao: marcarEdicaoConfig } = useConfigDocumentoSalva(
    usuarioLogado?.id,
    { nomeAmbulatorio, endereco, cidade, telefone },
    { setNomeAmbulatorio, setEndereco, setCidade, setTelefone },
    !!dadosIniciais?.config
  );
  const [mostrarConfig, setMostrarConfig] = useState(false);

  // ============================================
  // CARREGAR CONJUNTOS DO USUÁRIO DO FIREBASE
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;
    const carregarConjuntos = async () => {
      try {
        const q = query(
          collection(db, "conjuntosExames"),
          where("usuarioId", "==", usuarioLogado.id)
        );
        const snapshot = await getDocs(q);
        const conjuntos: ConjuntoExames[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ConjuntoExames[];
        setConjuntosUsuario(conjuntos);
      } catch (error) {
        console.error("Erro ao carregar conjuntos:", error);
      }
    };
    carregarConjuntos();
  }, [usuarioLogado]);

  // ============================================
  // BUSCA DE EXAMES
  // ============================================
  useEffect(() => {
    if (termoBusca.length >= 2) {
      const resultados = buscarExames(termoBusca).filter(
        // identidade = nome (o TUSS NÃO é único: ex. sódio sérico × urinário,
        // CA 19-9 × CA 125 compartilham código — B282/AI_122)
        (e) => !examesSelecionados.some((sel) => sel.nome === e.nome)
      );
      setResultadosBusca(resultados);
    } else {
      setResultadosBusca([]);
    }
  }, [termoBusca, examesSelecionados]);

  // ============================================
  // ADICIONAR/REMOVER EXAMES
  // ============================================
  const adicionarExame = (exame: ExameTUSS) => {
    setExamesSelecionados((prev) => {
      if (prev.some((e) => e.nome === exame.nome)) return prev;
      return [...prev, exame];
    });
    setTermoBusca("");
    setResultadosBusca([]);
  };
  const removerExame = (nome: string) => {
    setExamesSelecionados((prev) => prev.filter((e) => e.nome !== nome));
  };
  const aplicarConjunto = (conjunto: ConjuntoExames) => {
    setExamesSelecionados((prev) => {
      const novosExames: ExameTUSS[] = [];
      conjunto.exames.forEach((nomeExame) => {
        const exame = buscarExamePorNome(nomeExame);
        if (exame && !prev.some((e) => e.nome === exame.nome)) {
          novosExames.push(exame);
        }
      });
      return [...prev, ...novosExames];
    });
  };

  const limparTodos = () => {
    if (window.confirm("Limpar todos os exames selecionados?")) {
      setExamesSelecionados([]);
      setDadosGAL(null);
      setDadosCVPorForm({});
      setCvModalAbertoId(null);
      setDadosAPACPorExame({});
      setApacModalAbertoTuss(null);
    }
  };

  // ============================================
  // 🏥 FICHA GAL (B211) — detecção, abertura e geração
  // ============================================
  const tussSelecionados = examesSelecionados.map((e) => e.tuss);
  const formulariosDetectados = formulariosParaExames(tussSelecionados);
  const formGAL = formulariosDetectados.find((f) => f.tipo === "gal");
  // Todos os formulários de carga viral detectados (um cartão/modal por um).
  const formsCargaViral = formulariosDetectados.filter(
    (f) => f.tipo === "cargaViral"
  );
  // Dados do formulário cujo modal está aberto (alias usado pelo corpo do modal).
  const dadosCV = cvModalAbertoId ? dadosCVPorForm[cvModalAbertoId] ?? null : null;
  // APAC: um laudo POR exame de imagem que a exija (TC/RM/cintilografia/PET).
  const examesAPAC = examesSelecionados.filter((e) => exigeAPAC(e.tuss));
  // Dados do laudo cujo modal está aberto (alias usado pelo corpo do modal).
  const dadosAPAC = apacModalAbertoTuss
    ? dadosAPACPorExame[apacModalAbertoTuss] ?? null
    : null;

  const dataHojeBR = (): string => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  const abrirModalGAL = () => {
    setDadosGAL(
      (prev) =>
        prev ?? {
          exameSolicitado:
            "Citomegalovírus (CMV) — Carga Viral / PCR Quantitativo",
          codigoSUS: formGAL?.codigoSUS || "02.13.01.011-9",
          dataSolicitacao: dataHojeBR(),
          materialBiologico: "Sangue total (EDTA)",
          numeroAmostra: "Única",
          dataColeta: "",
          finalidade: "4 - Programa",
          tipoCaso: "3 - Acompanhamento",
          agravoDoenca:
            formGAL?.agravoDoenca || "Infecção pelo Citomegalovírus (CMV)",
          dataPrimeirosSintomas: "",
          tratamentoQuantidade: "",
          tratamentoUnidade: "",
          etapaTratamento: "",
          observacoes: "",
        }
    );
    setGalModalAberto(true);
  };

  const atualizarGAL = (campo: keyof DadosPedidoGAL, valor: string) => {
    setDadosGAL((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  };

  // dd/mm/aaaa a partir de uma data ISO (yyyy-mm-dd) ou já formatada.
  const formatarDataColeta = (data: string): string => {
    if (!data) return "";
    const m = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : data;
  };

  // 🏥 Carga viral (HBV/HCV/HIV) + Genotipagem do VHC — abre o modal com
  // valores iniciais sensatos. Para a genotipagem, pré-preenche o campo 40
  // (última quantificação do RNA do HCV) com a sorologia mais recente do
  // paciente que tenha HCV-RNA registrado (tabela de sorologias).
  const abrirModalCV = (form: FormularioAuxiliar) => {
    const config = configCargaViral(form.id);
    const ehGeno = config.id === "geno-hcv";
    // Última sorologia com HCV-RNA preenchido (já vem ordenada por data desc).
    const ultimaHCV = ehGeno
      ? listarSorologiasPorPaciente(paciente.id).find(
          (s) => s.hcvRNA && s.hcvRNA.trim()
        )
      : undefined;
    setDadosCVPorForm((prev) => {
      if (prev[form.id]) return prev; // mantém o que já foi preenchido
      return {
        ...prev,
        [form.id]: {
          formularioId: config.id,
          exameSolicitado: form.nome,
          codigoSUS: form.codigoSUS || config.codigoProcedimento,
          dataSolicitacao: dataHojeBR(),
          motivoExame: "",
          estagioDoencaCID: "",
          idadeGestacional: paciente.sexo === "M" ? "5. Não se aplica" : "",
          dataColeta: "",
          materialBiologico: "Sangue total (EDTA)",
          // Genotipagem do VHC — campo 40 puxado da sorologia mais recente
          genoCID: ehGeno ? "B18.2" : undefined,
          genoUltimaQuantData: ultimaHCV
            ? formatarDataColeta(ultimaHCV.dataColeta)
            : undefined,
          genoUltimaQuantUIML: ultimaHCV?.hcvRNA?.trim() || undefined,
        },
      };
    });
    setCvModalAbertoId(form.id);
  };

  const atualizarCV = (campo: keyof DadosCargaViral, valor: string) => {
    if (!cvModalAbertoId) return;
    setDadosCVPorForm((prev) => {
      const atual = prev[cvModalAbertoId];
      if (!atual) return prev;
      return { ...prev, [cvModalAbertoId]: { ...atual, [campo]: valor } };
    });
  };

  // Monta o solicitante com os dados FRESCOS do Firestore: o usuarioLogado da
  // sessão é uma foto do login — dadosSolicitante editado depois não chegaria.
  // Superset compatível com GAL e carga viral (cpfProfissional é o extra HBV).
  const obterSolicitante = async (): Promise<
    SolicitanteGAL & { cpfProfissional?: string }
  > => {
    let ds = usuarioLogado?.dadosSolicitante;
    try {
      if (usuarioLogado) {
        const snap = await getDoc(doc(db, "usuarios", usuarioLogado.id));
        if (snap.exists()) {
          ds = (snap.data() as any).dadosSolicitante || ds;
        }
      }
    } catch (e) {
      // sem rede/permissão: usa os dados da sessão mesmo
    }
    return {
      nomeProfissional: usuarioLogado?.nome || "",
      registroConselho: usuarioLogado?.registroProfissional
        ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
        : "",
      cnsProfissional: ds?.cnsProfissional,
      cpfProfissional: ds?.cpfProfissional,
      unidadeSaude: ds?.unidadeSaude,
      cnes: ds?.cnes,
      municipioAtendimento: ds?.municipioAtendimento,
      codigoIBGE: ds?.codigoIBGE,
      ufAtendimento: ds?.ufAtendimento,
    };
  };

  // Botões dos modais apenas SALVAM e fecham (os dados já são atualizados ao
  // vivo enquanto o usuário digita). A geração do PDF fica no botão principal
  // "Gerar Pedido de Exames (PDF)", que inclui o pedido comum + TODOS os
  // formulários preenchidos no mesmo documento — assim o usuário pode preencher
  // vários formulários antes de gerar (ex.: genotipagem + carga viral do HCV).
  const salvarGAL = () => setGalModalAberto(false);
  const salvarCV = () => setCvModalAbertoId(null);

  // APAC — abre/atualiza/salva. UM laudo por exame de imagem (keyed pelo TUSS).
  const abrirModalAPAC = (exame: ExameTUSS) => {
    setDadosAPACPorExame((prev) => {
      if (prev[exame.tuss]) return prev; // mantém o que já foi preenchido
      return {
        ...prev,
        [exame.tuss]: {
          dataSolicitacao: dataHojeBR(),
          diagnostico: "",
          cid10Principal: "",
          cid10Secundario: "",
          observacoes: "",
        },
      };
    });
    setApacModalAbertoTuss(exame.tuss);
  };
  const atualizarAPAC = (campo: keyof DadosAPAC, valor: string) => {
    if (!apacModalAbertoTuss) return;
    setDadosAPACPorExame((prev) => {
      const atual = prev[apacModalAbertoTuss];
      if (!atual) return prev;
      return { ...prev, [apacModalAbertoTuss]: { ...atual, [campo]: valor } };
    });
  };
  const salvarAPAC = () => setApacModalAbertoTuss(null);

  // ============================================
  // SALVAR NOVO CONJUNTO DO USUÁRIO
  // ============================================
  const salvarNovoConjunto = async () => {
    if (!nomeNovoConjunto.trim() || examesSelecionados.length === 0) {
      alert("Informe um nome e selecione pelo menos 1 exame.");
      return;
    }
    try {
      const novoConjunto = {
        nome: nomeNovoConjunto.trim(),
        tipo: "usuario",
        usuarioId: usuarioLogado?.id,
        usuarioNome: usuarioLogado?.nome,
        exames: examesSelecionados.map((e) => e.nome),
      };
      const docRef = await addDoc(
        collection(db, "conjuntosExames"),
        novoConjunto
      );
      setConjuntosUsuario([
        ...conjuntosUsuario,
        { ...novoConjunto, id: docRef.id } as ConjuntoExames,
      ]);
      setNomeNovoConjunto("");
      setMostrarNovoConjunto(false);
      alert("Conjunto salvo com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar conjunto:", error);
      alert("Erro ao salvar conjunto.");
    }
  };

  const excluirConjuntoUsuario = async (id: string) => {
    if (!window.confirm("Excluir este conjunto de exames?")) return;
    try {
      await deleteDoc(doc(db, "conjuntosExames", id));
      setConjuntosUsuario((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Erro ao excluir conjunto:", error);
    }
  };

  // ============================================
  // GERAR PDF
  // ============================================
  // Geração unificada: monta o pedido comum e anexa, no MESMO documento (folhas
  // seguintes), os formulários auxiliares preenchidos (GAL e/ou carga viral) —
  // um conjunto só. Inclui automaticamente o que estiver preenchido.
  // B289 (AI_127): prepara o pedido (validação + config + anexos auxiliares +
  // payload de histórico + selo opcional), compartilhado entre a impressão
  // (gerarDocumento) e a assinatura digital (handleAssinarPedidoExames). Fonte
  // única → o que se assina é o mesmo que se imprime. Retorna null se o usuário
  // cancelar (sem exame, ou recusou seguir com campo do cadastro em branco).
  const prepararPedido = async (
    paraAssinatura: boolean
  ): Promise<{
    configPDF: {
      nomeAmbulatorio: string;
      nomeMedico: string;
      crm: string;
      endereco: string;
      cidade: string;
    };
    examesParaPDF: {
      nome: string;
      tuss: string;
      categoria?: "laboratorial" | "imagem" | "outro";
      observacao?: string;
    }[];
    observacao?: string;
    anexos: { estilos: string; corpo: string }[];
    payload: Record<string, unknown>;
    selo?: string;
    codigoValidacao?: string;
  } | null> => {
    if (examesSelecionados.length === 0) {
      alert("Selecione pelo menos 1 exame.");
      return null;
    }
    const configPDF = {
      nomeAmbulatorio,
      nomeMedico: usuarioLogado?.nome || "Médico",
      crm: usuarioLogado?.registroProfissional
        ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
        : "",
      endereco,
      cidade,
    };

    // B289 (AI_127) — selo visível ICP-Brasil, só quando vai assinar. Versão
    // cheia p/ o rodapé do pedido; versão compacta p/ os campos de assinatura das
    // fichas SUS anexas (APAC 42, GAL 10, carga viral 5/7), como a LME 17/23.
    let seloPedido: string | undefined;
    let seloCompacto: string | undefined;
    let codigoValidacao: string | undefined;
    if (paraAssinatura) {
      const dadosSelo = {
        nome: usuarioLogado?.nome || "",
        registro: configPDF.crm,
        data: dataHoraSeloAgora(),
      };
      // B290 — QR de validação no rodapé (ao lado do selo grande ICP-Brasil).
      codigoValidacao = gerarCodigoValidacao();
      seloPedido =
        montarSeloAssinaturaDigital(dadosSelo) + montarBlocoQR(codigoValidacao);
      // B340 (AI_150) — o selo compacto das fichas do SUS (APAC 42, GAL 10,
      // carga viral 5/7) leva o QR do MESMO código do pedido: aqui só existe um
      // documento, então não há risco de apontar para outro (ver Receituario).
      seloCompacto = montarSeloAssinaturaDigital({
        ...dadosSelo,
        compacto: true,
        codigoValidacao,
      });
    }

    // Anexos dos formulários auxiliares (no mesmo documento de impressão) +
    // registro do que será salvo junto ao pedido (reaproveitamento).
    const anexos: { estilos: string; corpo: string }[] = [];
    const auxSalvos: {
      tipo: string;
      formularioId: string;
      tuss?: string;
      dados: any;
    }[] = [];
    // Todos os formulários de carga viral preenchidos (genotipagem, CV-HCV, etc.)
    const cvForms = Object.values(dadosCVPorForm);
    // APAC: um laudo por exame de imagem cujo modal foi preenchido/salvo.
    const apacAGerar = examesAPAC.filter((e) => dadosAPACPorExame[e.tuss]);
    if (dadosGAL || cvForms.length || apacAGerar.length) {
      const solicitante = await obterSolicitante();
      // Campos do cadastro exigidos por cada formulário: GAL pede CNS do
      // profissional; a carga viral (HBV) pede CPF do profissional; a APAC pede
      // um documento (CNS ou CPF) do profissional; todos pedem o CNES da unidade.
      const faltando: string[] = [];
      if (!solicitante.cnes) faltando.push("CNES da unidade");
      if (dadosGAL && !solicitante.cnsProfissional)
        faltando.push("CNS do profissional");
      if (cvForms.length && !solicitante.cpfProfissional)
        faltando.push("CPF do profissional");
      if (
        apacAGerar.length &&
        !solicitante.cpfProfissional &&
        !solicitante.cnsProfissional
      )
        faltando.push("CNS ou CPF do profissional (APAC)");
      if (faltando.length) {
        const seguir = window.confirm(
          `Atenção: estes dados do seu cadastro estão em branco e sairão vazios no formulário: ${faltando.join(
            ", "
          )}.\n\nPara preencher uma vez só: Gestão de Usuários → editar seu usuário → 'Dados para formulários do SUS'.\n\nDeseja gerar mesmo assim?`
        );
        if (!seguir) return null;
      }
      if (dadosGAL) {
        anexos.push(
          montarFragmentoFichaGAL(paciente, solicitante, dadosGAL, seloCompacto)
        );
        auxSalvos.push({
          tipo: "gal",
          formularioId: "gal-cmv",
          dados: dadosGAL,
        });
      }
      for (const dados of cvForms) {
        const config = configCargaViral(dados.formularioId);
        anexos.push(
          montarFragmentoCargaViral(
            paciente,
            solicitante,
            dados,
            config,
            seloCompacto
          )
        );
        auxSalvos.push({
          tipo: "cargaViral",
          formularioId: dados.formularioId,
          dados,
        });
      }
      for (const exame of apacAGerar) {
        const dados = dadosAPACPorExame[exame.tuss];
        anexos.push(
          montarFragmentoAPAC(
            paciente,
            solicitante,
            dados,
            [
              {
                nome: exame.nome,
                codigo: sigtapPorTuss(exame.tuss),
                quantidade: "1",
              },
            ],
            seloCompacto
          )
        );
        auxSalvos.push({
          tipo: "apac",
          formularioId: `apac-${exame.tuss}`,
          tuss: exame.tuss,
          dados,
        });
      }
    }

    const examesParaPDF = examesSelecionados.map((e) => ({
      nome: e.nome,
      tuss: e.tuss,
      categoria: e.categoria,
      observacao:
        e.categoria === "imagem" ? obsImagem[e.tuss] || undefined : undefined,
    }));

    // B289 (AI_127) — sanear undefined antes de gravar: a carga viral zera campos
    // opcionais (ex.: genoCID fora da genotipagem) como `undefined`, e o Firestore
    // rejeita `undefined` no addDoc. O serverTimestamp() entra DEPOIS do saneador
    // (sentinela do Firestore não pode ser reconstruída).
    const payload: Record<string, unknown> = {
      ...removerUndefined({
        pacienteId: paciente.id,
        tipo: "exames",
        titulo: `Pedido de Exames - ${paciente.nome}`,
        conteudo: {
          exames: examesSelecionados.map((e) => ({
            nome: e.nome,
            tuss: e.tuss,
            categoria: e.categoria,
          })),
          observacao: observacao || null,
          // 🏥 Formulários auxiliares salvos junto ao pedido (B211)
          formulariosAuxiliares: auxSalvos.length ? auxSalvos : null,
        },
        config: configPDF,
        criadoPor: usuarioLogado?.id || "",
        criadoPorNome: usuarioLogado?.nome || "",
      }),
      criadoEm: serverTimestamp(),
    };

    return {
      configPDF,
      examesParaPDF,
      observacao: observacao || undefined,
      anexos,
      payload,
      selo: seloPedido,
      codigoValidacao,
    };
  };

  // Limpa o editor após gerar/assinar (mesmo comportamento de antes).
  const limparPedido = () => {
    setExamesSelecionados([]);
    setObservacao("");
    setObsImagem({});
    setDadosGAL(null);
    setDadosCVPorForm({});
    setCvModalAbertoId(null);
    setDadosAPACPorExame({});
    setApacModalAbertoTuss(null);
  };

  // Geração unificada: monta o pedido + anexos e abre para impressão.
  const gerarDocumento = async () => {
    const prep = await prepararPedido(false);
    if (!prep) return;

    gerarPDFPedidoExames(
      paciente,
      prep.examesParaPDF,
      prep.configPDF,
      prep.observacao,
      prep.anexos.length ? prep.anexos : undefined
    );

    // Salvar no histórico do Firebase
    try {
      await addDoc(collection(db, "prescricoes"), prep.payload);
      if (onPrescricaoSalva) onPrescricaoSalva();
    } catch (error) {
      console.error("Erro ao salvar prescrição no histórico:", error);
    }
    limparPedido();
  };

  // B289 (AI_127): assina digitalmente o pedido de exames (médico-only + flag).
  // Monta o MESMO HTML da impressão (com selo), salva o pedido (captura o id para
  // o broker validar a autoria) e abre o ModalAssinatura.
  const handleAssinarPedidoExames = async () => {
    const prep = await prepararPedido(true);
    if (!prep) return;

    const html = montarPedidoExamesHTML(
      paciente,
      prep.examesParaPDF,
      prep.configPDF,
      prep.observacao,
      prep.anexos.length ? prep.anexos : undefined,
      prep.selo
    );

    let prescricaoId: string;
    try {
      const docRef = await addDoc(collection(db, "prescricoes"), prep.payload);
      prescricaoId = docRef.id;
      if (onPrescricaoSalva) onPrescricaoSalva();
    } catch (error) {
      console.error("Erro ao salvar o pedido para assinatura:", error);
      alert("Não foi possível salvar o pedido. Tente novamente.");
      return;
    }

    setModalAssinatura({
      html,
      tipo: "exames",
      prescricaoId,
      dados: {
        name: usuarioLogado?.nome || "",
        reason: "Pedido de Exames",
        location: cidade || "",
      },
      nomeArquivo: `pedido-exames-${paciente.nome}`.replace(/\s+/g, "_"),
      codigoValidacao: prep.codigoValidacao,
      dadosValidacao: {
        medicoNome: usuarioLogado?.nome || "",
        registro: prep.configPDF.crm,
        pacienteTelefone: telefoneVigente(paciente),
        pacienteNascimento: paciente.dataNascimento || "",
      },
    });

    limparPedido();
  };

  // Botão principal: inclui automaticamente os formulários já preenchidos.
  const handleGerarPDF = () => gerarDocumento();
  // ============================================
  // ESTILOS
  // ============================================
  const btnStyle: React.CSSProperties = {
    padding: "6px 12px",
    border: "none",
    borderRadius: theme.borderRadius.md,
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    fontFamily: theme.fonts.sans,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* ============================================ */}
      {/* CONFIGURAÇÃO DO CABEÇALHO */}
      {/* ============================================ */}
      <div
        style={{
          background: theme.gray[50],
          borderRadius: theme.borderRadius.md,
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setMostrarConfig(!mostrarConfig)}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: theme.gray[600],
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Settings size={12} /> Configuração do Documento
          </span>
          {mostrarConfig ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
        {mostrarConfig && (
          <div
            style={{
              marginTop: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Nome do Ambulatório
              </label>
              <input
                type="text"
                value={nomeAmbulatorio}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setNomeAmbulatorio(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Endereço
              </label>
              <input
                type="text"
                value={endereco}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setEndereco(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Cidade e UF
                </label>
                <input
                  type="text"
                  value={cidade}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setCidade(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Telefone
                </label>
                <input
                  type="text"
                  value={telefone}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setTelefone(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* CONJUNTOS PRÉ-SALVOS */}
      {/* ============================================ */}
      <div
        style={{
          background: "#eff6ff",
          borderRadius: theme.borderRadius.md,
          padding: "10px 12px",
          border: "1px solid #bfdbfe",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setMostrarConjuntos(!mostrarConjuntos)}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: theme.primary[700],
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Package size={14} /> Conjuntos de Exames
          </span>
          {mostrarConjuntos ? (
            <ChevronUp size={14} color={theme.primary[600]} />
          ) : (
            <ChevronDown size={14} color={theme.primary[600]} />
          )}
        </div>
        {mostrarConjuntos && (
          <div style={{ marginTop: "8px" }}>
            {/* CONJUNTOS DO SISTEMA */}
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                marginBottom: "8px",
              }}
            >
              {conjuntosPadrao.map((c) => (
                <button
                  key={c.id}
                  onClick={() => aplicarConjunto(c)}
                  style={{
                    ...btnStyle,
                    background: theme.primary[100],
                    color: theme.primary[700],
                    border: `1px solid ${theme.primary[300]}`,
                  }}
                  title={c.descricao}
                >
                  <Package size={12} /> {c.nome}
                </button>
              ))}
            </div>
            {/* CONJUNTOS DO USUÁRIO */}
            {conjuntosUsuario.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  flexWrap: "wrap",
                  marginBottom: "8px",
                }}
              >
                {conjuntosUsuario.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <button
                      onClick={() => aplicarConjunto(c)}
                      style={{
                        ...btnStyle,
                        background: "#f0fdf4",
                        color: "#166534",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <Package size={12} /> {c.nome}
                    </button>
                    <button
                      onClick={() => excluirConjuntoUsuario(c.id)}
                      style={{
                        padding: "4px",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#dc2626",
                      }}
                      title="Excluir conjunto"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* SALVAR COMO NOVO CONJUNTO */}
            {examesSelecionados.length > 0 && (
              <div>
                {mostrarNovoConjunto ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      value={nomeNovoConjunto}
                      onChange={(e) => setNomeNovoConjunto(e.target.value)}
                      placeholder="Nome do conjunto..."
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "12px",
                        fontFamily: theme.fonts.sans,
                      }}
                    />
                    <button
                      onClick={salvarNovoConjunto}
                      style={{
                        ...btnStyle,
                        background: "#16a34a",
                        color: "white",
                      }}
                    >
                      <Save size={12} /> Salvar
                    </button>
                    <button
                      onClick={() => setMostrarNovoConjunto(false)}
                      style={{
                        ...btnStyle,
                        background: theme.gray[300],
                        color: theme.gray[700],
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setMostrarNovoConjunto(true)}
                    style={{
                      ...btnStyle,
                      background: "transparent",
                      color: theme.primary[600],
                      border: `1px dashed ${theme.primary[400]}`,
                      fontSize: "11px",
                    }}
                  >
                    <Save size={12} /> Salvar seleção como novo conjunto
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* BUSCA DE EXAMES */}
      {/* ============================================ */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: `1px solid ${theme.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            padding: "6px 10px",
            background: "white",
          }}
        >
          <Search size={16} color={theme.gray[400]} />
          <input
            type="text"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            placeholder="Buscar exame por nome ou código TUSS..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "13px",
              fontFamily: theme.fonts.sans,
            }}
          />
          {termoBusca && (
            <button
              onClick={() => {
                setTermoBusca("");
                setResultadosBusca([]);
              }}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <X size={14} color={theme.gray[400]} />
            </button>
          )}
        </div>
        {/* RESULTADOS DA BUSCA */}
        {resultadosBusca.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: `1px solid ${theme.gray[300]}`,
              borderRadius: theme.borderRadius.md,
              boxShadow: theme.shadows.lg,
              zIndex: 50,
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {resultadosBusca.map((exame) => (
              <div
                key={exame.nome}
                onClick={() => adicionarExame(exame)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: `1px solid ${theme.gray[100]}`,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = theme.gray[50])
                }
                onMouseOut={(e) => (e.currentTarget.style.background = "white")}
              >
                <span>{exame.nome}</span>
                <span style={{ color: theme.gray[400], fontSize: "11px" }}>
                  TUSS: {exame.tuss}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* EXAMES SELECIONADOS */}
      {/* ============================================ */}
      {examesSelecionados.length > 0 && (
        <div
          style={{
            border: `1px solid ${theme.gray[200]}`,
            borderRadius: theme.borderRadius.md,
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: theme.gray[700],
                fontFamily: theme.fonts.sans,
              }}
            >
              <FlaskConical size={14} style={{ marginRight: "4px" }} />
              {examesSelecionados.length} exame
              {examesSelecionados.length > 1 ? "s" : ""} selecionado
              {examesSelecionados.length > 1 ? "s" : ""}
            </span>
            <button
              onClick={limparTodos}
              style={{
                ...btnStyle,
                background: "#fee2e2",
                color: "#dc2626",
                fontSize: "11px",
              }}
            >
              <Trash2 size={12} /> Limpar
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {examesSelecionados.map((exame) => (
              <div
                key={exame.nome}
                style={{
                  marginBottom: exame.categoria === "imagem" ? "6px" : "0",
                  // AI_118: exame de IMAGEM ocupa a linha inteira p/ a observação
                  // específica caber larga; chips de lab seguem inline (wrap).
                  flexBasis: exame.categoria === "imagem" ? "100%" : "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    background:
                      exame.categoria === "imagem"
                        ? "#eff6ff"
                        : theme.gray[100],
                    padding: "3px 8px",
                    borderRadius: "16px",
                    fontSize: "11px",
                    fontFamily: theme.fonts.sans,
                    color:
                      exame.categoria === "imagem"
                        ? "#1e40af"
                        : theme.gray[700],
                    border:
                      exame.categoria === "imagem"
                        ? "1px solid #bfdbfe"
                        : "none",
                  }}
                >
                  {exame.categoria === "imagem" && (
                    <span style={{ fontSize: "10px" }}>📷</span>
                  )}
                  {exame.nome}
                  <button
                    onClick={() => removerExame(exame.nome)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "0",
                      display: "flex",
                    }}
                  >
                    <X size={12} color={theme.gray[400]} />
                  </button>
                </div>
                {exame.categoria === "imagem" && (
                  <textarea
                    placeholder="Observação específica (ex: com sedação, avaliar trombose de porta...)"
                    value={obsImagem[exame.tuss] || ""}
                    onChange={(e) =>
                      setObsImagem((prev) => ({
                        ...prev,
                        [exame.tuss]: e.target.value,
                      }))
                    }
                    rows={2}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      marginTop: "3px",
                      border: `1px solid ${theme.gray[200]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "12px",
                      fontFamily: theme.fonts.sans,
                      color: theme.gray[700],
                      boxSizing: "border-box",
                      resize: "vertical",
                      minHeight: "48px",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* 🏥 FICHA GAL (B211) — aviso de formulário auxiliar */}
      {/* ============================================ */}
      {formGAL && (
        <div
          style={{
            border: "1px solid #bfdbfe",
            background: "#eff6ff",
            borderRadius: theme.borderRadius.md,
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#1e40af",
              fontFamily: theme.fonts.sans,
            }}
          >
            <strong>
              <FileText size={13} style={{ marginRight: "4px" }} />
              Este pedido exige a {formGAL.nome}.
            </strong>
            <div style={{ color: "#1d4ed8", marginTop: "2px" }}>
              {dadosGAL
                ? "Dados salvos. Gere o pedido no botão azul abaixo (sai junto)."
                : "Preencha e salve os dados da amostra; depois gere o pedido abaixo."}
            </div>
          </div>
          <button
            onClick={abrirModalGAL}
            style={{
              ...btnStyle,
              background: "#2563eb",
              color: "white",
              whiteSpace: "nowrap",
            }}
          >
            <FileText size={13} />
            {dadosGAL ? "Revisar ficha GAL" : "Preencher ficha GAL"}
          </button>
        </div>
      )}

      {/* ============================================ */}
      {/* 🏥 CARGA VIRAL / GENOTIPAGEM (B211) — um cartão por formulário */}
      {/* ============================================ */}
      {formsCargaViral.map((form) => {
        const preenchido = !!dadosCVPorForm[form.id];
        return (
          <div
            key={form.id}
            style={{
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              borderRadius: theme.borderRadius.md,
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#166534",
                fontFamily: theme.fonts.sans,
              }}
            >
              <strong>
                <FileText size={13} style={{ marginRight: "4px" }} />
                Este pedido exige o {form.nome}.
              </strong>
              <div style={{ color: "#15803d", marginTop: "2px" }}>
                {preenchido
                  ? "Dados salvos. Gere o pedido no botão abaixo (sai junto)."
                  : "Preencha e salve os dados clínicos; depois gere o pedido abaixo."}
              </div>
            </div>
            <button
              onClick={() => abrirModalCV(form)}
              style={{
                ...btnStyle,
                background: "#16a34a",
                color: "white",
                whiteSpace: "nowrap",
              }}
            >
              <FileText size={13} />
              {preenchido ? "Revisar formulário" : "Preencher formulário"}
            </button>
          </div>
        );
      })}

      {/* ============================================ */}
      {/* 🏥 APAC (B211) — imagem de média/alta complexidade */}
      {/* ============================================ */}
      {examesAPAC.map((exame) => {
        const preenchido = !!dadosAPACPorExame[exame.tuss];
        return (
          <div
            key={exame.nome}
            style={{
              border: "1px solid #fde68a",
              background: "#fffbeb",
              borderRadius: theme.borderRadius.md,
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#92400e",
                fontFamily: theme.fonts.sans,
              }}
            >
              <strong>
                <FileText size={13} style={{ marginRight: "4px" }} />
                APAC para: {exame.nome}
              </strong>
              <div style={{ color: "#b45309", marginTop: "2px" }}>
                {preenchido
                  ? "Dados salvos. Gere o pedido no botão abaixo (sai junto)."
                  : "Preencha diagnóstico, CID e indicação; depois gere o pedido abaixo."}
              </div>
            </div>
            <button
              onClick={() => abrirModalAPAC(exame)}
              style={{
                ...btnStyle,
                background: "#d97706",
                color: "white",
                whiteSpace: "nowrap",
              }}
            >
              <FileText size={13} />
              {preenchido ? "Revisar APAC" : "Preencher APAC"}
            </button>
          </div>
        );
      })}

      {/* ============================================ */}
      {/* OBSERVAÇÃO */}
      {/* ============================================ */}
      <div>
        <label
          style={{
            fontSize: "11px",
            fontWeight: "600",
            color: theme.gray[500],
            fontFamily: theme.fonts.sans,
            display: "block",
            marginBottom: "4px",
          }}
        >
          Observação / Indicação Clínica (opcional)
        </label>
        <textarea
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex: hepatopatia crônica, avaliação pré-transplante..."
          rows={2}
          style={{
            width: "100%",
            padding: "8px",
            border: `1px solid ${theme.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "12px",
            fontFamily: theme.fonts.sans,
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* ============================================ */}
      {/* BOTÃO GERAR PDF */}
      {/* ============================================ */}
      <button
        onClick={handleGerarPDF}
        disabled={examesSelecionados.length === 0}
        style={{
          padding: "10px 24px",
          background:
            examesSelecionados.length === 0 ? theme.gray[300] : "#16a34a",
          color: "white",
          border: "none",
          borderRadius: theme.borderRadius.md,
          fontSize: "14px",
          fontWeight: "700",
          cursor: examesSelecionados.length === 0 ? "not-allowed" : "pointer",
          fontFamily: theme.fonts.sans,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
        }}
      >
        <FileDown size={18} /> Gerar Pedido de Exames (PDF)
      </button>

      {/* B289 (AI_127): ASSINAR DIGITALMENTE (médico-only + flag de habilitação) */}
      {ehMedico(usuarioLogado) && ASSINATURA_HABILITADA && (
        <button
          onClick={handleAssinarPedidoExames}
          disabled={examesSelecionados.length === 0}
          style={{
            padding: "10px 24px",
            marginTop: "8px",
            background:
              examesSelecionados.length === 0
                ? theme.gray[300]
                : theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "14px",
            fontWeight: "700",
            cursor:
              examesSelecionados.length === 0 ? "not-allowed" : "pointer",
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <ShieldCheck size={18} /> Assinar digitalmente
        </button>
      )}

      {modalAssinatura && (
        <ModalAssinatura
          aberto={true}
          onFechar={() => setModalAssinatura(null)}
          html={modalAssinatura.html}
          tipo={modalAssinatura.tipo}
          prescricaoId={modalAssinatura.prescricaoId}
          dados={modalAssinatura.dados}
          nomeArquivo={modalAssinatura.nomeArquivo}
          codigoValidacao={modalAssinatura.codigoValidacao}
          dadosValidacao={modalAssinatura.dadosValidacao}
        />
      )}

      {/* PLACEHOLDER SE VAZIO */}
      {examesSelecionados.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "24px",
            color: theme.gray[400],
            fontSize: "13px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Selecione um conjunto acima ou busque exames individualmente
        </div>
      )}

      {/* ============================================ */}
      {/* 🏥 MODAL DA FICHA GAL (B211) */}
      {/* ============================================ */}
      {galModalAberto && dadosGAL && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={() => setGalModalAberto(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflowY: "auto",
              fontFamily: theme.fonts.sans,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: theme.gray[800],
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FileText size={16} /> Ficha GAL — Carga Viral de CMV
              </h3>
              <button
                onClick={() => setGalModalAberto(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <X size={18} color={theme.gray[400]} />
              </button>
            </div>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[500],
                margin: "0 0 16px 0",
              }}
            >
              Paciente e dados da unidade são preenchidos automaticamente.
              Confirme os dados da amostra abaixo.
            </p>

            {(() => {
              const labelStyle: React.CSSProperties = {
                fontSize: "11px",
                fontWeight: 600,
                color: theme.gray[600],
                display: "block",
                marginBottom: "3px",
              };
              const inputStyle: React.CSSProperties = {
                width: "100%",
                padding: "8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              };
              return (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Exame solicitado</label>
                    <input
                      type="text"
                      value={`${dadosGAL.exameSolicitado}${
                        dadosGAL.codigoSUS ? ` (${dadosGAL.codigoSUS})` : ""
                      }`}
                      readOnly
                      style={{ ...inputStyle, background: theme.gray[100] }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Data de solicitação</label>
                    <input
                      type="text"
                      value={dadosGAL.dataSolicitacao}
                      onChange={(e) =>
                        atualizarGAL("dataSolicitacao", e.target.value)
                      }
                      placeholder="dd/mm/aaaa"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Data da coleta (opcional)</label>
                    <input
                      type="text"
                      value={dadosGAL.dataColeta || ""}
                      onChange={(e) =>
                        atualizarGAL("dataColeta", e.target.value)
                      }
                      placeholder="dd/mm/aaaa"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Material biológico</label>
                    <input
                      type="text"
                      value={dadosGAL.materialBiologico}
                      onChange={(e) =>
                        atualizarGAL("materialBiologico", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Nº da amostra</label>
                    <input
                      type="text"
                      value={dadosGAL.numeroAmostra}
                      onChange={(e) =>
                        atualizarGAL("numeroAmostra", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Finalidade</label>
                    <select
                      value={dadosGAL.finalidade || ""}
                      onChange={(e) =>
                        atualizarGAL("finalidade", e.target.value)
                      }
                      style={inputStyle}
                    >
                      <option value="">—</option>
                      <option>1 - Campanha</option>
                      <option>2 - Inquérito</option>
                      <option>3 - Investigação</option>
                      <option>4 - Programa</option>
                      <option>5 - Protocolo</option>
                      <option>6 - Projeto</option>
                      <option>9 - Ignorado</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Tipo de caso (campo 44)</label>
                    <select
                      value={dadosGAL.tipoCaso || ""}
                      onChange={(e) => atualizarGAL("tipoCaso", e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">—</option>
                      <option>1 - Suspeito</option>
                      <option>2 - Comunicante</option>
                      <option>3 - Acompanhamento</option>
                      <option>4 - Controle</option>
                      <option>5 - Óbito</option>
                      <option>6 - Caso grave</option>
                      <option>7 - Surto</option>
                      <option>8 - Diagnóstico</option>
                      <option>9 - Ignorado</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Agravo/Doença (campo 39)</label>
                    <input
                      type="text"
                      value={dadosGAL.agravoDoenca || ""}
                      onChange={(e) =>
                        atualizarGAL("agravoDoenca", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Data dos primeiros sintomas (campo 40)
                    </label>
                    <input
                      type="text"
                      value={dadosGAL.dataPrimeirosSintomas || ""}
                      onChange={(e) =>
                        atualizarGAL("dataPrimeirosSintomas", e.target.value)
                      }
                      placeholder="dd/mm/aaaa"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Tratamento — tempo (campo 45)
                    </label>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <input
                        type="text"
                        value={dadosGAL.tratamentoQuantidade || ""}
                        onChange={(e) =>
                          atualizarGAL("tratamentoQuantidade", e.target.value)
                        }
                        placeholder="Qtd."
                        style={{ ...inputStyle, width: "60px", flex: "0 0 60px" }}
                      />
                      <select
                        value={dadosGAL.tratamentoUnidade || ""}
                        onChange={(e) =>
                          atualizarGAL("tratamentoUnidade", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 1 }}
                      >
                        <option value="">—</option>
                        <option>1 - Dia(s)</option>
                        <option>2 - Semana(s)</option>
                        <option>3 - Mês(es)</option>
                        <option>4 - Ano(s)</option>
                        <option>9 - Ignorado</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Etapa de tratamento (campo 46)
                    </label>
                    <select
                      value={dadosGAL.etapaTratamento || ""}
                      onChange={(e) =>
                        atualizarGAL("etapaTratamento", e.target.value)
                      }
                      style={inputStyle}
                    >
                      <option value="">—</option>
                      <option>1 - Pretratamento</option>
                      <option>2 - Tratamento</option>
                      <option>3 - Retratamento</option>
                      <option>4 - Avaliação de Resistência</option>
                      <option>9 - Ignorado</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Observações</label>
                    <textarea
                      value={dadosGAL.observacoes || ""}
                      onChange={(e) =>
                        atualizarGAL("observacoes", e.target.value)
                      }
                      rows={2}
                      style={{ ...inputStyle, resize: "vertical" }}
                    />
                  </div>
                </div>
              );
            })()}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                onClick={() => setGalModalAberto(false)}
                style={{
                  ...btnStyle,
                  background: theme.gray[200],
                  color: theme.gray[700],
                }}
              >
                Cancelar
              </button>
              <button
                onClick={salvarGAL}
                style={{ ...btnStyle, background: "#2563eb", color: "white" }}
              >
                <FileText size={13} /> Salvar ficha GAL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* 🏥 MODAL DA CARGA VIRAL HBV (B211) */}
      {/* ============================================ */}
      {cvModalAbertoId && dadosCV && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={() => setCvModalAbertoId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflowY: "auto",
              fontFamily: theme.fonts.sans,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: theme.gray[800],
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FileText size={16} /> {dadosCV.exameSolicitado}
              </h3>
              <button
                onClick={() => setCvModalAbertoId(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <X size={18} color={theme.gray[400]} />
              </button>
            </div>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[500],
                margin: "0 0 16px 0",
              }}
            >
              Paciente e dados da unidade são preenchidos automaticamente.
              Confirme os dados clínicos abaixo.
            </p>

            {(() => {
              const configCV = configCargaViral(dadosCV.formularioId);
              const labelStyle: React.CSSProperties = {
                fontSize: "11px",
                fontWeight: 600,
                color: theme.gray[600],
                display: "block",
                marginBottom: "3px",
              };
              const inputStyle: React.CSSProperties = {
                width: "100%",
                padding: "8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              };
              return (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Exame solicitado</label>
                    <input
                      type="text"
                      value={`${dadosCV.exameSolicitado}${
                        dadosCV.codigoSUS ? ` (${dadosCV.codigoSUS})` : ""
                      }`}
                      readOnly
                      style={{ ...inputStyle, background: theme.gray[100] }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Data da solicitação</label>
                    <input
                      type="text"
                      value={dadosCV.dataSolicitacao}
                      onChange={(e) =>
                        atualizarCV("dataSolicitacao", e.target.value)
                      }
                      placeholder="dd/mm/aaaa"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Data da coleta</label>
                    <input
                      type="text"
                      value={dadosCV.dataColeta || ""}
                      onChange={(e) =>
                        atualizarCV("dataColeta", e.target.value)
                      }
                      placeholder="opcional — o laboratório pode preencher"
                      style={inputStyle}
                    />
                  </div>
                  {/* Motivo: não existe na genotipagem do VHC */}
                  {dadosCV.formularioId !== "geno-hcv" && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelStyle}>Motivo do exame</label>
                      <select
                        value={dadosCV.motivoExame || ""}
                        onChange={(e) =>
                          atualizarCV("motivoExame", e.target.value)
                        }
                        style={inputStyle}
                      >
                        <option value="">—</option>
                        {configCV.motivosModal.map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Estágio/CID, idade gestacional e material biológico só
                      existem na carga viral padrão (HBV/HCV/Delta) */}
                  {["cv-hbv", "cv-hcv", "cv-hdv"].includes(
                    dadosCV.formularioId
                  ) && (
                    <>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Estágio da doença / CID</label>
                        <select
                          value={dadosCV.estagioDoencaCID || ""}
                          onChange={(e) =>
                            atualizarCV("estagioDoencaCID", e.target.value)
                          }
                          style={inputStyle}
                        >
                          <option value="">—</option>
                          {configCV.estagiosModal.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Idade gestacional</label>
                        <select
                          value={dadosCV.idadeGestacional || ""}
                          onChange={(e) =>
                            atualizarCV("idadeGestacional", e.target.value)
                          }
                          style={inputStyle}
                        >
                          <option value="">—</option>
                          {IDADE_GESTACIONAL_OPCOES.map((o) => (
                            <option key={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Material biológico</label>
                        <input
                          type="text"
                          value={dadosCV.materialBiologico || ""}
                          onChange={(e) =>
                            atualizarCV("materialBiologico", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                    </>
                  )}

                  {/* 🧬 HIV — Evidência laboratorial: 2 coletas de CV-HIV */}
                  {dadosCV.formularioId === "cv-hiv" && (
                    <>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label
                          style={{
                            ...labelStyle,
                            color: theme.gray[700],
                            marginTop: "2px",
                          }}
                        >
                          Evidência laboratorial — Carga Viral do HIV (CV-HIV).
                          Preencha as coletas que justificam o exame (opcional —
                          pode ser preenchido à mão).
                        </label>
                      </div>
                      <div>
                        <label style={labelStyle}>1ª coleta — data</label>
                        <input
                          type="text"
                          value={dadosCV.hivData1Coleta || ""}
                          onChange={(e) =>
                            atualizarCV("hivData1Coleta", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>1ª coleta — metodologia</label>
                        <input
                          type="text"
                          value={dadosCV.hivMetodologia1 || ""}
                          onChange={(e) =>
                            atualizarCV("hivMetodologia1", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>1º exame — nº cópias/mL</label>
                        <input
                          type="text"
                          value={dadosCV.hivCopias1 || ""}
                          onChange={(e) =>
                            atualizarCV("hivCopias1", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>1º exame — data do resultado</label>
                        <input
                          type="text"
                          value={dadosCV.hivDataResultado1 || ""}
                          onChange={(e) =>
                            atualizarCV("hivDataResultado1", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>2ª coleta — data</label>
                        <input
                          type="text"
                          value={dadosCV.hivData2Coleta || ""}
                          onChange={(e) =>
                            atualizarCV("hivData2Coleta", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>2ª coleta — metodologia</label>
                        <input
                          type="text"
                          value={dadosCV.hivMetodologia2 || ""}
                          onChange={(e) =>
                            atualizarCV("hivMetodologia2", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>2º exame — nº cópias/mL</label>
                        <input
                          type="text"
                          value={dadosCV.hivCopias2 || ""}
                          onChange={(e) =>
                            atualizarCV("hivCopias2", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>2º exame — data do resultado</label>
                        <input
                          type="text"
                          value={dadosCV.hivDataResultado2 || ""}
                          onChange={(e) =>
                            atualizarCV("hivDataResultado2", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                    </>
                  )}

                  {/* 🦠 Hepatite Delta — Teste Anti-HDV-Total (exigido p/ CV-HDV) */}
                  {dadosCV.formularioId === "cv-hdv" && (
                    <>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>
                          Teste Anti-HDV-Total — Resultado (obrigatório p/
                          autorizar a CV-HDV)
                        </label>
                        <select
                          value={dadosCV.antiHDVResultado || ""}
                          onChange={(e) =>
                            atualizarCV("antiHDVResultado", e.target.value)
                          }
                          style={inputStyle}
                        >
                          <option value="">—</option>
                          {ANTI_HDV_RESULTADO_OPCOES.map((o) => (
                            <option key={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Anti-HDV — Data da coleta
                        </label>
                        <input
                          type="text"
                          value={dadosCV.antiHDVDataColeta || ""}
                          onChange={(e) =>
                            atualizarCV("antiHDVDataColeta", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Anti-HDV — Data de liberação
                        </label>
                        <input
                          type="text"
                          value={dadosCV.antiHDVDataLiberacao || ""}
                          onChange={(e) =>
                            atualizarCV("antiHDVDataLiberacao", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                    </>
                  )}

                  {/* 🧬 Genotipagem do VHC — Peso, Número GAL, CID, última
                      quantificação do RNA do HCV (puxada da sorologia) e
                      simultaneidade com a carga viral */}
                  {dadosCV.formularioId === "geno-hcv" && (
                    <>
                      <div>
                        <label style={labelStyle}>Peso (kg) — só se &lt; 12 anos</label>
                        <input
                          type="text"
                          value={dadosCV.pesoPaciente || ""}
                          onChange={(e) =>
                            atualizarCV("pesoPaciente", e.target.value)
                          }
                          placeholder="opcional"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Número GAL</label>
                        <input
                          type="text"
                          value={dadosCV.numeroGAL || ""}
                          onChange={(e) =>
                            atualizarCV("numeroGAL", e.target.value)
                          }
                          placeholder="opcional"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>CID10</label>
                        <input
                          type="text"
                          value={dadosCV.genoCID || ""}
                          onChange={(e) =>
                            atualizarCV("genoCID", e.target.value)
                          }
                          placeholder="B18.2"
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label
                          style={{ ...labelStyle, color: theme.gray[700] }}
                        >
                          Última quantificação do RNA do HCV (campo 40)
                          {dadosCV.genoUltimaQuantUIML
                            ? " — puxada da última sorologia; confira/edite"
                            : " — sem sorologia com HCV-RNA; preencha à mão"}
                        </label>
                      </div>
                      <div>
                        <label style={labelStyle}>RNA do HCV — Data</label>
                        <input
                          type="text"
                          value={dadosCV.genoUltimaQuantData || ""}
                          onChange={(e) =>
                            atualizarCV("genoUltimaQuantData", e.target.value)
                          }
                          placeholder="dd/mm/aaaa"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>RNA do HCV — UI/mL</label>
                        <input
                          type="text"
                          value={dadosCV.genoUltimaQuantUIML || ""}
                          onChange={(e) =>
                            atualizarCV("genoUltimaQuantUIML", e.target.value)
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>RNA do HCV — Log</label>
                        <input
                          type="text"
                          value={dadosCV.genoUltimaQuantLog || ""}
                          onChange={(e) =>
                            atualizarCV("genoUltimaQuantLog", e.target.value)
                          }
                          placeholder="opcional"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Solicitação simultânea de carga viral e genotipagem?
                        </label>
                        <select
                          value={dadosCV.genoSolicitacaoSimultanea || ""}
                          onChange={(e) =>
                            atualizarCV(
                              "genoSolicitacaoSimultanea",
                              e.target.value
                            )
                          }
                          style={inputStyle}
                        >
                          <option value="">—</option>
                          <option>1. Sim</option>
                          <option>2. Não</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Coleta simultânea de carga viral e genotipagem?
                        </label>
                        <select
                          value={dadosCV.genoColetaSimultanea || ""}
                          onChange={(e) =>
                            atualizarCV("genoColetaSimultanea", e.target.value)
                          }
                          style={inputStyle}
                        >
                          <option value="">—</option>
                          <option>1. Sim</option>
                          <option>2. Não</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                onClick={() => setCvModalAbertoId(null)}
                style={{
                  ...btnStyle,
                  background: theme.gray[200],
                  color: theme.gray[700],
                }}
              >
                Cancelar
              </button>
              <button
                onClick={salvarCV}
                style={{ ...btnStyle, background: "#16a34a", color: "white" }}
              >
                <FileText size={13} /> Salvar formulário
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* 🏥 MODAL APAC (B211) */}
      {/* ============================================ */}
      {apacModalAbertoTuss && dadosAPAC && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={() => setApacModalAbertoTuss(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflowY: "auto",
              fontFamily: theme.fonts.sans,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: theme.gray[800],
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FileText size={16} /> Laudo APAC
                {examesAPAC.find((e) => e.tuss === apacModalAbertoTuss)
                  ? ` — ${
                      examesAPAC.find((e) => e.tuss === apacModalAbertoTuss)!
                        .nome
                    }`
                  : ""}
              </h3>
              <button
                onClick={() => setApacModalAbertoTuss(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <X size={18} color={theme.gray[400]} />
              </button>
            </div>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[500],
                margin: "0 0 12px 0",
              }}
            >
              Paciente e dados da unidade são preenchidos automaticamente. Este
              exame entra como o procedimento solicitado deste laudo.
            </p>

            {/* Procedimento deste laudo (somente leitura) */}
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: theme.borderRadius.md,
                padding: "8px 10px",
                marginBottom: "14px",
                fontSize: "12px",
                color: "#92400e",
              }}
            >
              <strong>Procedimento solicitado:</strong>{" "}
              {examesAPAC.find((e) => e.tuss === apacModalAbertoTuss)?.nome}
            </div>

            {(() => {
              const labelStyle: React.CSSProperties = {
                fontSize: "11px",
                fontWeight: 600,
                color: theme.gray[600],
                display: "block",
                marginBottom: "3px",
              };
              const inputStyle: React.CSSProperties = {
                width: "100%",
                padding: "8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              };
              return (
                <div style={{ display: "grid", gap: "12px" }}>
                  <div>
                    <label style={labelStyle}>
                      Descrição do diagnóstico (campo 33)
                    </label>
                    <textarea
                      value={dadosAPAC.diagnostico}
                      onChange={(e) =>
                        atualizarAPAC("diagnostico", e.target.value)
                      }
                      rows={2}
                      style={{ ...inputStyle, resize: "vertical" }}
                      placeholder="Ex.: Cirrose hepática por VHC, em investigação de nódulo hepático"
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label style={labelStyle}>CID10 principal (campo 34)</label>
                      <input
                        type="text"
                        value={dadosAPAC.cid10Principal}
                        onChange={(e) =>
                          atualizarAPAC("cid10Principal", e.target.value)
                        }
                        style={inputStyle}
                        placeholder="Ex.: K74.6"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        CID10 secundário (campo 35, opcional)
                      </label>
                      <input
                        type="text"
                        value={dadosAPAC.cid10Secundario || ""}
                        onChange={(e) =>
                          atualizarAPAC("cid10Secundario", e.target.value)
                        }
                        style={inputStyle}
                        placeholder="Ex.: B18.2"
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Observações — indicação do exame (campo 37)
                    </label>
                    <textarea
                      value={dadosAPAC.observacoes}
                      onChange={(e) =>
                        atualizarAPAC("observacoes", e.target.value)
                      }
                      rows={3}
                      style={{ ...inputStyle, resize: "vertical" }}
                      placeholder="Justificativa clínica / indicação do exame de imagem"
                    />
                  </div>
                </div>
              );
            })()}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                onClick={() => setApacModalAbertoTuss(null)}
                style={{
                  ...btnStyle,
                  background: theme.gray[200],
                  color: theme.gray[700],
                }}
              >
                Cancelar
              </button>
              <button
                onClick={salvarAPAC}
                style={{ ...btnStyle, background: "#d97706", color: "white" }}
              >
                <FileText size={13} /> Salvar APAC
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
